import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import { checkNested } from 'srcReact/helpers/validator'
import ReactTable from 'srcReact/components/common2/ReactTable'
import { organizationSettingsActions } from 'srcReact/OrganizationSettingsPage'
import ReactMenu from 'srcReact/components/common2/ReactMenu'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import { getWorkplaceRoleColor, getWorkplaceMemberSince } from 'srcReact/helpers/utils'
import DesktopHeader from '../DesktopHeader'
import NotFound from 'srcReact/components/common2/NotFound'
import { getCurrentOrgId, isOrganizationManager } from 'srcReact/helpers/organizationHelper'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import TransferRoleModal from 'srcReact/Settings/components/TransferRoleModal'
import { commonActions } from 'srcReact/components'
import Avatar from 'srcReact/components/common2/Avatar'
import ErrorFallback from 'srcReact/ErrorPages/ErrorFallback'
import LightProfileModal from 'srcReact/components/common2/LightProfileModal'

const Members = ({ deactivated = 0, requestToDelete = 0 }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    login: { user: loggedInUser },
    common: { isMobile },
    organization: { current_organization },
    organizationSettings: {
      member: { members, stats, loading, error },
      deleteAccData: { data, loading: deleteAccLoading, error: deleteAccError },
      refresh
    }
  } = useSelector((state) => state)
  const [searchText] = useState('')
  const [orderby] = useState('lastname')
  const [showTransferRoleModal, setShowTransferRoleModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [modalData, setModalData] = useState({
    isShowUserModal: false,
    selectedModalUser: {}
  })

  useEffect(() => {
    if (checkNested(current_organization, 'info', 'id')) {
      dispatch(
        organizationSettingsActions.getOrganizationMembers(
          current_organization.info.id,
          searchText,
          orderby,
          deactivated,
          requestToDelete
        )
      )
    }
  }, [
    current_organization && current_organization.info && current_organization.info.id,
    refresh,
    deactivated,
    requestToDelete
  ])

  useEffect(() => {
    if (data) {
      if (data.deleted === 0) {
        setShowTransferRoleModal(true)
        dispatch(commonActions.setTransferRoleData(data))
      } else if (data.delete === 1) {
        setShowTransferRoleModal(false)
      }
    }
  }, [data])

  const handleUpdateMemberRole = (row, action) => {
    let payload = { resourceKey: row.resourceKey, action, roles: 'workplace-manager' }
    dispatch(organizationSettingsActions.updateMemberRole(row.workplace_ids, payload, 'member'))
  }
  const handleRemoveMember = (row) => {
    dispatch(organizationSettingsActions.removeMember(row.workplace_ids, row.resourceKey))
  }

  const reinstateUser = (row) => {
    const orgId = getCurrentOrgId()
    dispatch(organizationSettingsActions.reinstateAccount(orgId, row.resourceKey))
  }
  const deleteUser = (row) => {
    const orgId = getCurrentOrgId()
    setUserToDelete(row)
    dispatch(organizationSettingsActions.deleteAccount(orgId, row.resourceKey))
  }

  const options = (row) => {
    let role = row.roles.split(',')[0]
    let isLoggedInUser = row.resourceKey === loggedInUser.id
    let actions = []
    actions.push({
      text: t('organizationSettings.members.actions.viewProfile'),
      onClick: () => showUserModal(row)
    })
    if (!isLoggedInUser && role === 'workplace-member' && deactivated !== 1 && requestToDelete !== 1) {
      actions.push({
        text: t('organizationSettings.members.actions.makeWorkplaceManager'),
        onClick: () => handleUpdateMemberRole(row, 'add')
      })
    }
    if (!isLoggedInUser && role === 'workplace-manager' && deactivated !== 1 && requestToDelete !== 1) {
      actions.push({
        text: t('organizationSettings.members.actions.removeRole'),
        onClick: () => handleUpdateMemberRole(row, 'remove')
      })
    }
    if (deactivated !== 1) {
      actions.push({
        text: t('organizationSettings.members.actions.sendMessage'),
        onClick: () => showUserModal(row, 'messenger')
      })
      if (!isLoggedInUser && role === 'workplace-member' && requestToDelete !== 1) {
        actions.push({
          text: t('organizationSettings.members.actions.removeMember'),
          onClick: () => handleRemoveMember(row)
        })
      }
      if (requestToDelete === 1) {
        actions.push({
          text: t('global.deleteUser'),
          onClick: () => deleteUser(row)
        })
      }
    } else {
      actions.push({
        text: t('global.reinstate'),
        onClick: () => reinstateUser(row)
      })
    }
    const orgActions = getOrgManagerActions(row)
    if (orgActions.length > 0) {
      actions = actions.concat(orgActions)
    }

    return actions
  }

  const updateOrgRole = (userId, role, action) => {
    const orgId = getCurrentOrgId()
    dispatch(organizationSettingsActions.updateOrgRole(orgId, userId, role, action))
  }

  const showUserModal = (user, screen = 'main') => {
    setModalData({
      isShowUserModal: true,
      selectedModalUser: user,
      screen: screen
    })
  }

  const hideUserModal = () => {
    setModalData({
      ...modalData,
      isShowUserModal: false
    })
  }

  const getOrgManagerActions = (user) => {
    let options = []
    let isLoggedInUser = user.resourceKey === loggedInUser.id
    if (isOrganizationManager(current_organization) && !isLoggedInUser) {
      if (user.organization_role?.includes('account_owner')) {
        options.push({
          text: t('organizationSettings.members.actions.removeOrgAdmin'),
          onClick: () => updateOrgRole(user.workplace_member_id, 'account_owner', 'remove')
        })
      } else {
        options.push({
          text: t('organizationSettings.members.actions.makeOrgAdmin'),
          onClick: () => updateOrgRole(user.workplace_member_id, 'account_owner', 'add')
        })
      }
      if (user.organization_role?.includes('technical_support')) {
        options.push({
          text: t('organizationSettings.members.actions.removeITSupport'),
          onClick: () => updateOrgRole(user.workplace_member_id, 'technical_support', 'remove')
        })
      } else {
        options.push({
          text: t('organizationSettings.members.actions.makeITSupport'),
          onClick: () => updateOrgRole(user.workplace_member_id, 'technical_support', 'add')
        })
      }
    }
    return options
  }

  const getOptionsBasedOnRole = () => {
    let options = getOrgManagerActions(modalData.selectedModalUser)
    return options
  }

  const columns = useMemo(
    () => [
      {
        Header: t('organizationSettings.members.columns.member'),
        id: 'name',
        accessor: (row, idx) => (
          <div role='button' className={styles.name} onClick={() => showUserModal(row)}>
            <Avatar
              imgWrapperSize='tiny'
              userId={row.resourceKey}
              profilePicture={row.profile_picture}
              firstname={row.firstname}
              lastname={row.lastname}
              mimeType={row.mime_type}
              originalTag={row.original_tag}
              isOriginal={row.is_original}
              containerCSS={styles.picture}
              showImageAsDecorative={true}
              onClickAction={() => showUserModal(row)}
              withExpertiseGroup={row.with_expertise_group}
            />
            <div>{row.firstname + ' ' + row.lastname}</div>
          </div>
        )
      },
      {
        Header: t('organizationSettings.members.columns.role'),
        id: 'roles',
        accessor: (row, idx) => {
          const orgRoles = row.organization_role?.length > 0 ? row.organization_role.split(',') : []
          const wpRoles = row.roles?.split(',') || []
          const allRoles = [...orgRoles, wpRoles.length > 0 ? wpRoles[0] : []] //take only first role in workplace roles
          const allRolesText = allRoles.map((role) => t(`global.roles.${role.replace('-', '_')}`))
          return (
            <div
              className={`${styles.role} ${row.organization_role ? styles.organizationRole : ''}`}
              style={{ backgroundColor: getWorkplaceRoleColor(row.roles) }}>
              {allRolesText.join(', ')}
            </div>
          )
        }
      },
      {
        Header: t('organizationSettings.leadershipTeam.columns.workplaceName'),
        id: 'workplace_names',
        accessor: (row, idx) => <div className={styles.workplaceName}>{row.workplace_names}</div>,
        className: styles.rowWorkplaceName
      },
      {
        Header: t('organizationSettings.members.columns.memberSince'),
        id: 'created_at',
        accessor: (row, idx) => <div className={styles.memberSince}>{getWorkplaceMemberSince(row.created_at)}</div>,
        className: styles.rowMemberSince
      },
      {
        Header: t('organizationSettings.members.columns.email'),
        accessor: 'email',
        className: styles.rowEmail
      },
      {
        Header: '',
        id: 'options',
        accessor: (row, idx) => <ReactMenu options={options(row)} />
      }
    ],
    [deactivated, requestToDelete]
  )

  const handleCurrentPage = (page) => {
    let offset = (page - 1) * appConstantSettings.WORKPLACE_PAGINATION_QUANTITY
    dispatch(
      organizationSettingsActions.getOrganizationMembers(
        current_organization.info.id,
        searchText,
        orderby,
        deactivated,
        requestToDelete,
        offset
      )
    )
  }
  const handleSearch = (searchValue) => {
    dispatch(
      organizationSettingsActions.getOrganizationMembers(
        current_organization.info.id,
        searchValue,
        orderby,
        deactivated,
        requestToDelete
      )
    )
  }

  const hideTransferRoleModal = () => {
    setShowTransferRoleModal(false)
  }

  return (
    <div className={styles.membersWrapper}>
      {!isMobile && (
        <DesktopHeader
          title={
            deactivated === 1
              ? t('organizationSettings.deactivatedMembers.title')
              : requestToDelete === 1
              ? t('organizationSettings.deleteRequest.title')
              : t('global.members')
          }
          subtitle={
            deactivated === 1
              ? t('organizationSettings.deactivatedMembers.subtitle')
              : requestToDelete === 1
              ? t('organizationSettings.deleteRequest.subtitle')
              : t('organizationSettings.members.subtitle')
          }
        />
      )}
      {error && error === appConstantSettings.HTTP500 ? (
        <ErrorFallback className={styles.errorFallback} isServerError={true} />
      ) : members && members.length > 0 ? (
        <React.Fragment>
          {error && <ErrorMessage text={error} className={styles.error} />}
          <SearchTextBox
            value={searchText}
            {...(searchText.length === 0 && { clear: true })}
            minimumSearchChar={appConstantSettings.SEARCH_LENGTH - 1}
            size='large'
            iconPosition='start'
            placeholder={t('global.searchPlaceholder')}
            handleSearch={handleSearch}
            cssClass={styles.searchInputContainer}
            inputCssClass={styles.searchInput}
          />
          <ReactTable
            columns={columns}
            data={members}
            loading={loading}
            className={styles.tableClassName}
            handleCurrentPage={handleCurrentPage}
            pagination={{ total: stats.total_members, offset: stats.next_offset }}
          />
        </React.Fragment>
      ) : !loading ? (
        <NotFound title={t('global.no_results')} />
      ) : null}
      {showTransferRoleModal && (
        <TransferRoleModal
          userToDelete={userToDelete}
          type='delete'
          showModal={showTransferRoleModal}
          hideModal={hideTransferRoleModal}
          screen='select-group'
        />
      )}
      {modalData.isShowUserModal && (
        <LightProfileModal
          show={modalData.isShowUserModal}
          userIdwithRole={modalData.selectedModalUser.resourceKey}
          roleManagementOptions={getOptionsBasedOnRole()}
          closeModalWithRole={hideUserModal}
          screenWithRole={modalData.screen}
        />
      )}
    </div>
  )
}

export default Members
